import { doesGridFitPage } from '@/lib/guides/columns-rows-fit'
import { MIN_COLUMNS, MIN_ROWS } from '../constants/guide-limits'
import type { ColumnsRowsSettings } from '../types/columns-rows-settings'
import type { GuideUnit } from '../types/guide-unit'
import { getCountError, parseCountInput } from './count-input'
import { parseGutterToPoints } from './gutter-parsing'

export type GuideSettingsInputs = {
  columnsInput: string
  rowsInput: string
  colGutterInput: string
  rowGutterInput: string
}

export type GuideSettingsValidation = {
  columns: number | null
  rows: number | null
  /** The settings Apply would send, or null while any field is invalid or overflows the page. */
  previewSettings: ColumnsRowsSettings | null
  columnCountMessage: string | null
  rowCountMessage: string | null
  colGutterMessage: string | null
  rowGutterMessage: string | null
}

/** Parses the four dialog fields and derives the Apply payload plus each field's message. */
export function validateGuideSettings(
  { columnsInput, rowsInput, colGutterInput, rowGutterInput }: GuideSettingsInputs,
  unit: GuideUnit,
  pageInnerWidth: number,
  pageInnerHeight: number
): GuideSettingsValidation {
  const columns = parseCountInput(columnsInput)
  const rows = parseCountInput(rowsInput)
  const colGutterPoints = parseGutterToPoints(colGutterInput, unit).points
  const rowGutterPoints = parseGutterToPoints(rowGutterInput, unit).points
  const columnCountError = getCountError(columnsInput, columns, MIN_COLUMNS)
  const rowCountError = getCountError(rowsInput, rows, MIN_ROWS)

  const hasColOverflow =
    columns != null && colGutterPoints != null
      ? !doesGridFitPage(columns, colGutterPoints, pageInnerWidth)
      : false

  const hasRowOverflow =
    rows != null && rowGutterPoints != null
      ? !doesGridFitPage(rows, rowGutterPoints, pageInnerHeight)
      : false

  const previewSettings =
    columns == null ||
    rows == null ||
    columnCountError != null ||
    rowCountError != null ||
    colGutterPoints == null ||
    rowGutterPoints == null ||
    hasColOverflow ||
    hasRowOverflow
      ? null
      : { columns, rows, colGutter: colGutterPoints, rowGutter: rowGutterPoints }

  const colGutterError =
    colGutterInput.trim() === '' || colGutterPoints == null ? 'Invalid value' : null
  const rowGutterError =
    rowGutterInput.trim() === '' || rowGutterPoints == null ? 'Invalid value' : null

  return {
    columns,
    rows,
    previewSettings,
    columnCountMessage: columnCountError ?? (hasColOverflow ? 'Does not fit on page' : null),
    rowCountMessage: rowCountError ?? (hasRowOverflow ? 'Does not fit on page' : null),
    colGutterMessage: colGutterError ?? (hasColOverflow ? 'Does not fit on page' : null),
    rowGutterMessage: rowGutterError ?? (hasRowOverflow ? 'Does not fit on page' : null),
  }
}
